import { combineReducers } from '@reduxjs/toolkit';
import { createReducer } from 'typesafe-actions';
import { v4 as uuidv4 } from 'uuid';

import { TabResourceList } from '@portal/common/types';

import _ from '../../locale';
import { GROUP_CONTENT_ID } from '../../router/paths';
import {
    deleteOrganizationResourceListAsync,
    getPublicResourceListsAsync,
    makeResourceListDefaultAsync,
    makeResourceListNotDefaultAsync,
    makeResourceListPrivateAsync,
} from '../resource-lists/actions';
import {
    addDashboardTab,
    changeDataCollection,
    dismissWelcomeMessage,
    initializeDataCollection,
    minimizeSidebar,
    postponeSurvey,
    removeDashboardTab,
    setQueryBuilderCollapsed,
    setSidebarMinimized,
    toggleMinimizeSidebar,
    updateDashboardTab,
} from './actions';

export const FALLBACK_DEFAULT_TAB: TabResourceList = {
    id: GROUP_CONTENT_ID,
    name: _.get('default_group_list_name'),
    tabId: uuidv4(),
    is_default: true,
    is_public: true,
};

const replaceDefaultTab = (
    currentState: TabResourceList[],
    newDefaultList: any
): TabResourceList[] => {
    const isExistingTab = currentState.find((tab) => tab.id === newDefaultList.id);

    if (isExistingTab) {
        return currentState.map((tab) => {
            if (tab.id === newDefaultList.id) {
                return { ...tab, is_default: true };
            } else if (tab.is_default) {
                return { ...tab, is_default: false };
            }
            return tab;
        });
    } else {
        const newDefaultTab: TabResourceList = { ...newDefaultList, tabId: uuidv4() };

        let isDefaultReplaced = false;
        return currentState.map((tab) => {
            if (tab.is_default && !isDefaultReplaced) {
                isDefaultReplaced = true;
                return newDefaultTab;
            } else if (tab.is_default && isDefaultReplaced) {
                return { ...tab, is_default: false };
            }
            return tab;
        });
    }
};

type State = {
    isSidebarMinimized: boolean;
    isQueryBuilderCollapsed: boolean;
    dismissedWelcomeMessageHash: null | string;
    selectedDataCollectionIdByDataTool: null | Record<string, number>;
    dashboardTabs: TabResourceList[];
    isSurveyPostponed: boolean;
};

const initialState: State = {
    isSidebarMinimized: false,
    isQueryBuilderCollapsed: false,
    dismissedWelcomeMessageHash: null,
    selectedDataCollectionIdByDataTool: null,
    dashboardTabs: [FALLBACK_DEFAULT_TAB],
    isSurveyPostponed: false,
};

export default combineReducers<State>({
    isSurveyPostponed: createReducer(initialState.isSurveyPostponed).handleAction(
        postponeSurvey,
        (state, action) => action.payload
    ),
    isSidebarMinimized: createReducer(initialState.isSidebarMinimized)
        .handleAction(toggleMinimizeSidebar, (state) => !state)
        .handleAction(setSidebarMinimized, (state, action) => action.payload)
        .handleAction(minimizeSidebar, () => true),
    isQueryBuilderCollapsed: createReducer(initialState.isQueryBuilderCollapsed).handleAction(
        setQueryBuilderCollapsed,
        (state, action) => action.payload
    ),
    dismissedWelcomeMessageHash: createReducer(
        initialState.dismissedWelcomeMessageHash
    ).handleAction(dismissWelcomeMessage, (state, action) => action.payload),
    selectedDataCollectionIdByDataTool: createReducer(
        initialState.selectedDataCollectionIdByDataTool
    ).handleAction([changeDataCollection, initializeDataCollection], (state, action) => {
        const { dataTool, dataCollectionId } = action.payload;
        return {
            ...state,
            [dataTool]: dataCollectionId,
        };
    }),
    dashboardTabs: createReducer(initialState.dashboardTabs)
        .handleAction(addDashboardTab, (state, action) => state.concat(action.payload))
        .handleAction(updateDashboardTab, (state, action) =>
            state.map((i, index) => (index === action.payload.index ? action.payload.list : i))
        )
        .handleAction(removeDashboardTab, (state, action) =>
            state.filter((i, index) => index !== action.payload)
        )
        .handleAction(getPublicResourceListsAsync.success, (state, action) => {
            const defaultTab = state.find((tab) => tab.is_default);
            const defaultList = action.payload.find((list) => list.is_default);

            // If default tab is present and it's different from new default list then replace it
            if (defaultTab != null && defaultList != null && defaultTab.id !== defaultList.id) {
                return replaceDefaultTab(state, defaultList);
            }

            return state;
        })
        .handleAction(makeResourceListDefaultAsync.success, (state, action) => {
            const defaultTab = state.find((tab) => tab.is_default);
            const defaultList = action.payload;

            // If default tab is present and it's different from new default list then replace it
            if (defaultTab != null && defaultList != null && defaultTab.id != defaultList.id) {
                return replaceDefaultTab(state, defaultList);
            }

            return state;
        })
        .handleAction(makeResourceListNotDefaultAsync.success, (state, action) => {
            return state.map((tab) => (tab.is_default ? FALLBACK_DEFAULT_TAB : tab));
        })
        .handleAction(makeResourceListPrivateAsync.success, (state, action) => {
            const defaultTab = state.find((tab) => tab.is_default);
            // if default tab list was turned private reset to fallback
            if (defaultTab && defaultTab.id === action.payload.id) {
                return state.map((tab) => (tab.is_default ? FALLBACK_DEFAULT_TAB : tab));
            }
            return state;
        })
        .handleAction(deleteOrganizationResourceListAsync.success, (state, action) => {
            const listId = action.payload.listId;
            const tabs = state.filter((tab) => tab.id !== listId);

            if (tabs.length === 0) {
                return [FALLBACK_DEFAULT_TAB];
            }
            return tabs;
        }),
});
